import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined, CheckOutlined, MenuOutlined, CloseOutlined,
  RocketOutlined, AppstoreOutlined, KeyOutlined, ThunderboltOutlined,
  ArrowLeftOutlined, SettingOutlined, MonitorOutlined,
  SafetyOutlined, DatabaseOutlined, LinkOutlined,
  BulbOutlined,
} from '@ant-design/icons';

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: <DatabaseOutlined /> },
  { id: 'quick-start', label: 'Inicio Rápido', icon: <RocketOutlined /> },
  { id: 'engines', label: 'Motores Soportados', icon: <AppstoreOutlined /> },
  { id: 'instances', label: 'Tipos de Instancia', icon: <SettingOutlined /> },
  { id: 'config', label: 'Configuración', icon: <SettingOutlined /> },
  { id: 'connection', label: 'Conexión', icon: <LinkOutlined /> },
  { id: 'security', label: 'Reglas de Seguridad', icon: <SafetyOutlined /> },
  { id: 'statuses', label: 'Estados del Servicio', icon: <ThunderboltOutlined /> },
  { id: 'events', label: 'Eventos', icon: <MonitorOutlined /> },
  { id: 'best-practices', label: 'Buenas Prácticas', icon: <BulbOutlined /> },
];

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative group rounded-lg overflow-hidden my-4">
      {language && <div className="bg-gray-800 dark:bg-gray-950 px-4 py-2 text-xs text-gray-400 font-mono border-b border-gray-700">{language}</div>}
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 px-4 py-4 text-sm font-mono overflow-x-auto leading-relaxed"><code>{code}</code></pre>
      <button onClick={handleCopy} className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100" title="Copiar">
        {copied ? <CheckOutlined className="text-green-400" /> : <CopyOutlined />}
      </button>
    </div>
  );
};

const DocsDatabase: React.FC = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.reduce((closest, section) =>
        section.top <= 120 && section.top > closest.top ? section : closest,
        { id: offsets[0].id, top: -Infinity }
      );
      setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setMobileMenuOpen(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <img src="/favicon-logo.png" alt="Kubiy" className="w-5 h-5" />
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">Kubiy Cloud</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700"></div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Databases</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <ArrowLeftOutlined /> Volver al inicio
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
              {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`${mobileMenuOpen ? 'fixed inset-0 top-16 z-20 bg-white dark:bg-gray-900 p-6' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto lg:py-8`}>
            <nav className="space-y-1">
              {SECTIONS.map((section) => (
                <button key={section.id} onClick={() => scrollTo(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeSection === section.id ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}>
                  <span className="text-base">{section.icon}</span>{section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0 py-8 lg:py-12">
            <div className="max-w-3xl">

              {/* INTRO */}
              <section id="intro" className="mb-16 scroll-mt-24">
                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full text-xs font-semibold">Documentación</span>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-3 mb-4">Databases</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Kubiy Databases te permite crear y gestionar bases de datos administradas en la nube usando Amazon RDS.
                  Elige tu motor, versión, instancia y seguridad. Kubiy se encarga de la infraestructura, backups y mantenimiento.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <DatabaseOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Multi-Motor</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">PostgreSQL, MySQL, MariaDB, SQL Server, Oracle</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <SafetyOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Seguridad</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">IPs permitidas, credenciales seguras, VPC aislada</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <KeyOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Credenciales</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Recupera usuario y contraseña master en cualquier momento</p>
                  </div>
                </div>
              </section>

              {/* INICIO RÁPIDO */}
              <section id="quick-start" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Inicio Rápido</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Crea tu primera base de datos en 4 pasos:</p>
                <div className="space-y-6">
                  {[
                    { n: 1, title: 'Motor y versión', desc: 'Asigna un nombre, selecciona el motor (PostgreSQL, MySQL, etc.) y elige la versión.' },
                    { n: 2, title: 'Configura la instancia', desc: 'Elige tipo de instancia, almacenamiento (mín. 20 GB), backups y Multi-AZ.' },
                    { n: 3, title: 'Seguridad', desc: 'Define puerto, usuario master y si tendrá acceso público. Restringe IPs después de crear.' },
                    { n: 4, title: 'Revisa y crea', desc: 'Confirma la configuración. Después del pago, Kubiy provisiona en 5-15 minutos.' },
                  ].map((s) => (
                    <div key={s.n} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">{s.n}</div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* MOTORES */}
              <section id="engines" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Motores Soportados</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Motor</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Puerto</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['PostgreSQL', '5432', 'Relacional open source avanzada. JSON, extensiones, tipos complejos.'],
                        ['MySQL', '3306', 'La más popular del mundo. Amplia compatibilidad.'],
                        ['MariaDB', '3306', 'Fork de MySQL con mejoras de rendimiento. Compatible con MySQL.'],
                        ['SQL Server', '1433', 'Microsoft. Ideal para ecosistema .NET y Windows.'],
                        ['Oracle', '1521', 'Empresarial. Cargas críticas con HA y seguridad avanzada.'],
                      ].map(([engine, port, desc]) => (
                        <tr key={engine}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{engine}</td>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{port}</code></td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* TIPOS DE INSTANCIA */}
              <section id="instances" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tipos de Instancia</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">vCPU</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">RAM</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Caso de uso</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['db.t3.micro', '2', '1 GB', 'Desarrollo, pruebas'],
                        ['db.t3.small', '2', '2 GB', 'Apps ligeras, microservicios'],
                        ['db.t3.medium', '2', '4 GB', 'Apps web medianas'],
                        ['db.t3.large', '2', '8 GB', 'Producción estándar'],
                        ['db.r5.large', '2', '16 GB', 'Uso intensivo de memoria'],
                        ['db.r5.xlarge', '4', '32 GB', 'DBs grandes de producción'],
                      ].map(([t, cpu, ram, use]) => (
                        <tr key={t}>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{t}</code></td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">{cpu}</td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">{ram}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{use}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* CONFIGURACIÓN */}
              <section id="config" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Configuración</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Parámetro</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Default</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Almacenamiento', 'Tamaño del volumen en GB', '20 GB'],
                        ['Backups', 'Snapshots automáticos diarios', 'Activado'],
                        ['Multi-AZ', 'Réplica en otra zona para HA', 'Desactivado'],
                        ['Acceso público', 'Permite conexiones desde internet', 'Desactivado'],
                        ['Puerto', 'Puerto TCP del motor', 'Según motor'],
                        ['Usuario master', 'Administrador de la DB', 'Requerido'],
                      ].map(([param, desc, def_]) => (
                        <tr key={param}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{param}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td>
                          <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{def_}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Multi-AZ</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Crea una réplica síncrona en otra zona de disponibilidad. En caso de fallo, el failover es automático.
                  Recomendado para producción crítica.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acceso Público</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Si está activado, la DB es accesible desde internet usando el endpoint DNS, pero controlada por la lista de IPs permitidas.
                  Si está desactivado, solo es accesible desde la VPC del tenant.
                </p>
              </section>

              {/* CONEXIÓN */}
              <section id="connection" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Conexión</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">DNS de Kubiy</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Kubiy asigna un DNS personalizado persistente a cada base de datos. Se muestra en el resumen del servicio.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Credenciales Master</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Recupera usuario y contraseña en cualquier momento desde <strong>Recuperar Credenciales</strong> en el detalle del servicio.
                  La contraseña se muestra oculta y se puede revelar temporalmente.
                </p>
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-6">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Importante:</strong> Las credenciales master tienen acceso completo. Crea usuarios adicionales con permisos limitados para las aplicaciones.
                  </p>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Ejemplos de Conexión</h3>
                <CodeBlock language="bash" code={`# PostgreSQL\npsql -h TU_DNS_KUBIY -p 5432 -U tu_usuario -d postgres\n\n# MySQL\nmysql -h TU_DNS_KUBIY -P 3306 -u tu_usuario -p`} />
                <CodeBlock language="text" code={`# Connection strings\npostgresql://tu_usuario:tu_password@TU_DNS_KUBIY:5432/postgres\nmysql://tu_usuario:tu_password@TU_DNS_KUBIY:3306/mydb`} />
              </section>

              {/* REGLAS DE SEGURIDAD */}
              <section id="security" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Reglas de Seguridad</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  El acceso a la base de datos se controla mediante una lista de IPs permitidas en formato CIDR.
                  Solo las direcciones incluidas pueden conectarse.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Gestión desde el Dashboard</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Agregar IP/CIDR manualmente</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>Mi IP:</strong> agrega tu IP actual como /32 automáticamente</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Eliminar IPs de la lista</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Guardar cambios para aplicarlos</li>
                </ul>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Formato CIDR</h3>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Formato</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Significado</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['IP/32', 'Una sola IP específica (ej: 203.0.113.50/32)'],
                        ['IP/24', 'Rango de 256 IPs — subred clase C (ej: 192.168.1.0/24)'],
                        ['0.0.0.0/0', 'Cualquier IP (acceso abierto, NO recomendado)'],
                      ].map(([f, d]) => (
                        <tr key={f}>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{f}</code></td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400">
                    <strong>Importante:</strong> Sin IPs en la lista, la DB no es accesible externamente. Con <code className="font-mono">0.0.0.0/0</code> es accesible desde cualquier IP (no recomendado para producción).
                  </p>
                </div>
              </section>

              {/* ESTADOS */}
              <section id="statuses" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Estados del Servicio</h2>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Estado</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Indicador</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['initializing', 'bg-yellow-400 animate-pulse', 'Se provisiona red e instancia RDS (5-15 min)'],
                        ['deploying', 'bg-blue-400 animate-pulse', 'CloudFormation configurando infraestructura'],
                        ['running', 'bg-green-500', 'Activa y aceptando conexiones'],
                        ['stopped', 'bg-gray-400', 'Detenida. Datos conservados, costo reducido'],
                        ['starting', 'bg-green-400 animate-pulse', 'Arrancando después de estar detenida'],
                        ['stopping', 'bg-gray-400 animate-pulse', 'En proceso de detención'],
                        ['restarting', 'bg-blue-400 animate-pulse', 'Reiniciando instancia RDS'],
                        ['error', 'bg-red-500', 'Error en creación o ejecución'],
                        ['deleting', 'bg-red-400 animate-pulse', 'Eliminando todos los recursos'],
                      ].map(([status, color, desc]) => (
                        <tr key={status}>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{status}</code></td>
                          <td className="py-3 px-4"><span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}></span></td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acciones por Estado</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Acción</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Disponible en</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Iniciar', 'stopped', 'Arranca la DB detenida'],
                        ['Detener', 'running', 'Detiene sin eliminar datos'],
                        ['Reiniciar', 'running', 'Reinicia la instancia RDS'],
                        ['Credenciales', 'running', 'Recupera usuario y contraseña master'],
                        ['Eliminar', 'Todos (excepto deleting)', 'Elimina permanentemente'],
                      ].map(([a, avail, d]) => (
                        <tr key={a}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{a}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{avail}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* EVENTOS */}
              <section id="events" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Eventos</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Registran acciones y cambios de estado. Pestaña <strong>Eventos</strong> en el detalle del servicio.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { type: 'info', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', desc: 'Progreso de operaciones' },
                    { type: 'success', color: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300', desc: 'Operación completada' },
                    { type: 'warning', color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', desc: 'Requiere atención' },
                    { type: 'error', color: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300', desc: 'Error en operación' },
                  ].map((e) => (
                    <div key={e.type} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.color}`}>{e.type}</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{e.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* BUENAS PRÁCTICAS */}
              <section id="best-practices" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Buenas Prácticas</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Seguridad</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Nunca uses <code className="font-mono">0.0.0.0/0</code> en producción</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Crea usuarios adicionales con permisos limitados</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> No compartas credenciales master; úsalas solo para administración</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Restringe IPs con notación /32 para máxima seguridad</li>
                </ul>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Rendimiento</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Elige instancias db.r5 para cargas con uso intensivo de memoria</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Monitorea CPU y memoria desde los eventos</li>
                </ul>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Disponibilidad</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Activa Multi-AZ para producción crítica</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Activa backups automáticos siempre</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Usa el DNS de Kubiy en lugar de IP directa</li>
                </ul>
              </section>

            </div>
          </main>
        </div>
      </div>

      <footer className="border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">&copy; {new Date().getFullYear()} Kubiy Cloud. Todos los derechos reservados.</span>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">kubiy.cloud</Link>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Términos</a>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsDatabase;